import {
  SemanticEvidencePanel,
  answerHighlights,
  highlightedText,
  type EvidenceReference,
  type EvidenceSource,
  type UnsupportedClaim,
} from "./SemanticEvidencePanel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  SemanticAnswerComparison,
  type AnswerChoice,
  type AnswerEffect,
} from "./SemanticAnswerComparison";
import { semanticReviewRequest } from "./semanticReviewRequest";
import {
  SemanticMergeDiagnostics,
  type MergeCheck,
} from "./SemanticMergeDiagnostics";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  answerReviewStatus,
  automaticallyApprovedQuestions,
  reviewAnswerGroups,
  availabilityLabels,
  checkLabels,
  subjectiveChecks,
  reviewActionExplanation,
  reviewQuestionKey,
  uniqueReviewQuestions,
  mergeReviewQuestions,
} from "./semanticReviewModel";

interface Check {
  needsEvidenceReview?: boolean;
  evidenceFailure?:
    | "citation_not_found"
    | "unsupported_claim"
    | "missing_evidence";
  unsupportedClaims?: UnsupportedClaim[];
  status: "passed" | "failed" | "missing";
  reason: string;
}
function VerificationCheck({ label, check }: { label: string; check: Check }) {
  if (check.status === "passed") {
    return (
      <Typography variant="body2" sx={{ my: 1 }}>
        <strong>{label}</strong> — passed: {check.reason}
      </Typography>
    );
  }
  const failed = check.status === "failed" && !check.needsEvidenceReview;
  const statusLabel = check.needsEvidenceReview
    ? "Needs evidence review"
    : failed
      ? "Failed"
      : "Not checked";
  return (
    <Alert
      severity={failed ? "error" : "warning"}
      role="group"
      aria-label={`${label}: ${statusLabel}`}
      sx={{
        my: 1,
        border: 1,
        borderColor: failed ? "error.main" : "warning.main",
        overflowWrap: "anywhere",
      }}
    >
      <AlertTitle sx={{ fontSize: "0.875rem", fontWeight: 700, mb: 0.5 }}>
        {label} — {statusLabel}
      </AlertTitle>
      <Typography variant="body2">{check.reason}</Typography>
    </Alert>
  );
}
interface Content {
  answerText: string;
  questionExamples: string[];
  evidence: EvidenceReference[];
  checks: Record<string, Check>;
  questionChecks: Array<Check & { question: string }>;
  verificationState: string;
}
interface GroupingProposal {
  targetId: string;
  matchedQuestion?: string;
  targetReviewVersion: number;
  relation: "equivalent" | "conflict";
  reason: string;
  mergeCheck?: MergeCheck;
  content?: Content;
}
interface WordingConflict {
  answerId: string;
  reviewVersion: number;
  question: string;
  answerText: string;
  wordings: string[];
}
interface Answer extends Content {
  questionGroupId?: string;
  grouping?: {
    status: string;
    mergedInto?: string;
    proposals: GroupingProposal[];
  } | null;
  wordingConflicts?: WordingConflict[];
  processingStatus?: string;
  processingStage?: string;
  processingError?: string;
  id: string;
  question: string;
  source: "predicted" | "session";
  availability: string;
  availabilityReason: string;
  reviewVersion: number;
  published: Content | null;
  draft: Content | null;
  occurrenceCount: number;
  usageCount: number;
  conversationContext?: Array<{ question: string; response: string }>;
  reviewHistory: Array<{
    questions?: string[];
    removedWordings?: string[];
    after?: Partial<Content>;
    mergeCheck?: MergeCheck;
    evidenceExplanation?: string;
    actor: string;
    at: string;
    action: string;
    note: string;
  }>;
}
interface ReviewData {
  sourceEvidence?: EvidenceSource;
  status: string;
  libraryRevision: string;
  answers: Answer[];
  error?: string;
  coverage?: Record<string, unknown>;
  artifacts?: Record<string, unknown>;
}
const apiBase = import.meta.env.VITE_API_URL || "http://localhost:3001";
const sourceLabels = {
  predicted: "Predicted",
  session: "From diagnosis sessions",
};
const statusHelpSx = {
  cursor: "help",
  textDecoration: "underline",
  textDecorationStyle: "dotted",
  textUnderlineOffset: "3px",
};
export function SemanticReviewDialog({
  caseId,
  title,
  onClose,
}: {
  caseId: string;
  title: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<ReviewData | null>(null);
  const [selected, setSelected] = useState("");
  const [promotedAnswerId, setPromotedAnswerId] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("current");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [hideRejected, setHideRejected] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [answerText, setAnswerText] = useState("");
  const [questions, setQuestions] = useState("");
  const [note, setNote] = useState("");
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [supportReviewOpen, setSupportReviewOpen] = useState(false);
  const [overrides, setOverrides] = useState<string[]>([]);

  const lifecycle = useRef(new AbortController());
  const dirty = useRef(false);
  const answerInput = useRef<HTMLTextAreaElement | null>(null);
  const questionList = useRef<HTMLUListElement | null>(null);
  const editingId = useRef("");
  const readEpoch = useRef(0);
  const lastProgress = useRef({ id: "", checking: false });
  useEffect(() => {
    lifecycle.current = new AbortController();
    return () => lifecycle.current.abort();
  }, []);
  const acceptData = useCallback((next: ReviewData) => {
    setData(next);
    setSelected((current) =>
      next.answers.some((a) => a.id === current)
        ? current
        : next.answers[0]?.id || "",
    );
  }, []);
  const showMergedAnswer = (targetId: string) => {
    setSourceFilter("all");
    setFilter("current");
    setSearch("");
    setSelected(targetId);
    setPromotedAnswerId(targetId);
    if (questionList.current) questionList.current.scrollTop = 0;
  };
  useEffect(() => {
    if (questionList.current) questionList.current.scrollTop = 0;
  }, [promotedAnswerId]);
  const load = useCallback(
    async (signal?: AbortSignal) => {
      const epoch = ++readEpoch.current;
      const payload = await semanticReviewRequest(
        `${apiBase}/api/cases/${encodeURIComponent(caseId)}/semantic/review`,
        { signal: signal || lifecycle.current.signal },
      );
      if (
        epoch === readEpoch.current &&
        !signal?.aborted &&
        !lifecycle.current.signal.aborted
      )
        acceptData(payload.data);
    },
    [caseId, acceptData],
  );
  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal).catch((e) => {
      if (!controller.signal.aborted) setError(e.message);
    });
    return () => controller.abort();
  }, [load]);
  const answer = data?.answers.find((a) => a.id === selected);
  const content = answer?.draft || answer;
  const reviewStatus = answer ? answerReviewStatus(answer) : undefined;
  const storedQuestions = (
    content?.questionChecks?.length
      ? content.questionChecks.map((q) => q.question)
      : content?.questionExamples || []
  ).join("\n");
  const unresolvedAlternative =
    answer?.grouping?.proposals.some(
      (p) =>
        p.relation === "conflict" &&
        data?.answers.some(
          (a) =>
            a.id === p.targetId &&
            a.availability !== "outdated" &&
            a.availability !== "rejected" &&
            (a.published || (a.draft || a).verificationState === "passed"),
        ),
    ) || false;
  const editsChanged =
    answerText !== (content?.answerText || "") || questions !== storedQuestions;
  const checking =
    answer?.processingStatus === "pending" ||
    answer?.processingStatus === "processing";
  useEffect(() => {
    if (
      lastProgress.current.id === selected &&
      lastProgress.current.checking &&
      !checking &&
      answer
    ) {
      setMessage(
        answer.grouping?.status === "merged" &&
          answer.reviewHistory[answer.reviewHistory.length - 1]?.action ===
            "questions_auto_merged"
          ? "Equivalent questions merged automatically. The existing patient answer is unchanged."
          : answer.processingStatus === "failed"
            ? "Background checking failed. Review the error below and use Save and check to retry."
            : answer.availability === "rejected"
              ? "Answer rejected. It will not be reused."
              : answer.draft?.verificationState === "failed" ||
                  (!answer.published && answer.verificationState === "failed")
                ? "Checks finished. Review the issues before approving; any previously published answer is unchanged."
                : answer.source === "session" &&
                    answer.draft?.verificationState === "passed"
                  ? "Checks finished. The edited answer is awaiting administrator approval."
                  : answer.availability === "available_approved"
                    ? "Answer approved and available."
                    : answer.availability === "available_automatically"
                      ? "Checks finished. The answer is available automatically."
                      : answer.availability === "awaiting_approval"
                        ? "Checks finished. The verified answer is awaiting administrator approval."
                        : "Checks finished. Review the issues before approving.",
      );
    }
    lastProgress.current = { id: selected, checking };
  }, [selected, checking, answer]);
  useEffect(() => {
    if (editingId.current === selected && dirty.current) return;
    const changedSelection = editingId.current !== selected;
    editingId.current = selected;
    dirty.current = false;
    setAnswerText(content?.answerText || "");
    setQuestions(
      (content?.questionChecks?.length
        ? content.questionChecks.map((q) => q.question)
        : content?.questionExamples || []
      ).join("\n"),
    );
    if (changedSelection) {
      setEvidenceOpen(false);
      setOverrides([]);

      setNote("");
    }
  }, [content, selected]);
  const backgroundWork = data?.answers.some((a) =>
    ["pending", "processing"].includes(a.processingStatus || ""),
  );
  useEffect(() => {
    if (!backgroundWork || busy) return;
    const controller = new AbortController();
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        await load(controller.signal);
      } catch (e) {
        if (active)
          setError(
            e instanceof Error ? e.message : "Unable to refresh progress",
          );
      }
      if (active) timer = setTimeout(() => void poll(), 4000);
    };
    timer = setTimeout(() => void poll(), 2000);
    return () => {
      active = false;
      controller.abort();
      clearTimeout(timer);
    };
  }, [backgroundWork, busy, load]);
  const questionGroups = useMemo(
    () => reviewAnswerGroups(data?.answers || []),
    [data],
  );
  const automaticQuestionsByAnswer = useMemo(
    () =>
      new Map(
        (data?.answers || []).map((a) => [
          a.id,
          automaticallyApprovedQuestions(a),
        ]),
      ),
    [data],
  );
  const automaticQuestions = automaticQuestionsByAnswer.get(selected) || [];
  const lastAutomaticMerge = [...(answer?.reviewHistory || [])]
    .reverse()
    .find(
      (event) =>
        event.action === "questions_auto_merged" &&
        event.after?.answerText === answer?.published?.answerText,
    );
  const selectedQuestionGroup = questionGroups.find(
    (group) => group.answer.id === (answer?.questionGroupId || answer?.id),
  );
  const groupMembers =
    selectedQuestionGroup?.members.filter(
      (member) => !hideRejected || member.availability !== "rejected",
    ) || [];
  const visible = useMemo(
    () =>
      questionGroups
        .filter((group) =>
          group.members.some(
            (a) =>
              (!hideRejected || a.availability !== "rejected") &&
              (sourceFilter === "all" || a.source === sourceFilter) &&
              (filter === "all" ||
                (filter === "current" && a.availability !== "outdated") ||
                (filter === "review" && !!answerReviewStatus(a).attention) ||
                (filter === "awaiting_approval" &&
                  answerReviewStatus(a).attention === "approval") ||
                (filter === "needs_correction" &&
                  answerReviewStatus(a).attention === "correction") ||
                (!["awaiting_approval", "needs_correction"].includes(filter) &&
                  a.availability === filter) ||
                (filter === "available" &&
                  a.availability.startsWith("available"))) &&
              `${a.question} ${a.answerText} ${a.questionExamples.join(" ")}`
                .toLowerCase()
                .includes(search.toLowerCase()),
          ),
        )
        .map((group) => group.answer)
        .sort(
          (a, b) =>
            Number(b.id === promotedAnswerId) -
            Number(a.id === promotedAnswerId),
        ) || [],
    [
      questionGroups,
      filter,
      sourceFilter,
      hideRejected,
      search,
      promotedAnswerId,
    ],
  );
  useEffect(() => {
    if (
      !visible.some((a) => a.id === (answer?.questionGroupId || selected)) ||
      (hideRejected && answer?.availability === "rejected")
    )
      setSelected(
        visible.find((a) => a.id === answer?.questionGroupId)?.id ||
          visible[0]?.id ||
          "",
      );
  }, [
    visible,
    selected,
    answer?.questionGroupId,
    answer?.availability,
    hideRejected,
  ]);
  const request = async (path: string, body: unknown) => {
    return semanticReviewRequest(
      `${apiBase}/api/cases/${encodeURIComponent(caseId)}/semantic/${path}`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: lifecycle.current.signal,
      },
    );
  };
  const conflictsFor = (targetId?: string): WordingConflict[] => {
    if (!answer || !data) return [];
    if (!targetId) return answer.wordingConflicts || [];
    const proposal = answer.grouping?.proposals.find(
      (p) => p.targetId === targetId,
    );
    const keys = new Set(
      (proposal?.content?.questionExamples || []).map(reviewQuestionKey),
    );
    return data.answers
      .filter(
        (a) =>
          a.id !== targetId &&
          a.id !== answer.id &&
          a.published &&
          a.availability.startsWith("available"),
      )
      .flatMap((a) => {
        const wordings = a.published!.questionExamples.filter((q) =>
          keys.has(reviewQuestionKey(q)),
        );
        return wordings.length
          ? [
              {
                answerId: a.id,
                reviewVersion: a.reviewVersion,
                question: a.question,
                answerText: a.published!.answerText,
                wordings,
              },
            ]
          : [];
      });
  };
  const act = async (
    action: "validate" | "approve" | "reject" | "merge",
    targetId?: string,
    resolveConflicts: WordingConflict[] = [],
    selectedEvidence?: EvidenceReference[],
    mergeQuestions?: string[],
    evidenceExplanation?: string,
  ): Promise<boolean> => {
    if (!data || !answer) return false;
    setBusy(true);
    readEpoch.current++;
    setError("");
    setMessage("");
    try {
      const payload = await request(`answers/${answer.id}/review`, {
        revision: data.libraryRevision,
        reviewVersion: answer.reviewVersion,
        action,
        selectedEvidence,
        evidenceExplanation:
          action === "validate" ? evidenceExplanation : undefined,
        mergeQuestions: action === "merge" ? mergeQuestions : undefined,
        targetId,
        resolveConflicts:
          action === "approve" || action === "merge"
            ? resolveConflicts.map((c) => ({
                answerId: c.answerId,
                reviewVersion: c.reviewVersion,
              }))
            : undefined,
        note,
        answerText:
          action === "reject" || action === "merge" ? undefined : answerText,
        questionExamples:
          action === "reject" || action === "merge"
            ? undefined
            : questions
                .split("\n")
                .map((q) => q.trim())
                .filter(Boolean),
        overrideChecks: action === "approve" ? overrides : [],
      });
      dirty.current = false;
      acceptData(payload.data);
      if (action === "merge" && targetId) showMergedAnswer(targetId);
      setMessage(
        payload.data.reviewQueued
          ? action === "approve"
            ? "Approval queued while publication data is prepared. You can close this window; progress will refresh automatically."
            : evidenceExplanation
              ? "Reassessment queued. Your explanation and evidence will be checked against the complete answer. Progress will refresh automatically."
              : "Edits saved. Verification is running in the background. You can close this window; progress will refresh automatically."
          : action === "merge"
            ? "Question examples combined. Diagnosis will use the existing patient answer. The collected exchange remains in review history."
            : action === "reject"
              ? "Answer rejected. It will not be reused."
              : action === "approve"
                ? "Answer approved and available."
                : answer.source === "session"
                  ? "Edits saved and checked. Review the results, then approve the answer."
                  : "Edits saved and checked. Review the availability and any issues below.",
      );
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Review failed");
      return false;
    } finally {
      setBusy(false);
    }
  };
  const regenerate = async () => {
    setBusy(true);
    readEpoch.current++;
    setError("");
    setMessage("");
    try {
      await request("regenerate", {});
      await load();
      setMessage(
        "Prediction queued. Current available answers stay in use until the replacement is published. Refresh to check progress.",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Generation failed");
    } finally {
      setBusy(false);
    }
  };
  const issues = Object.entries(content?.checks || {}).filter(
    ([, c]) => c.status !== "passed",
  );
  const detailedChecks = [
    ...Object.values(content?.checks || {}),
    ...(content?.questionChecks || []),
  ];
  const failedCheckCount = detailedChecks.filter(
    (check) => check.status === "failed" && !check.needsEvidenceReview,
  ).length;
  const evidenceReviewCount = detailedChecks.filter(
    (check) => check.status !== "passed" && check.needsEvidenceReview,
  ).length;
  const uncheckedCount = detailedChecks.filter(
    (check) => check.status === "missing",
  ).length;
  const unavailable = answer?.availability === "outdated";
  const approvalBlocked = unavailable
    ? "This answer is outdated."
    : checking
      ? "Checking is still running. You can keep editing or close this window."
      : editsChanged
        ? "Your edits need checking. Select Save and check first."
        : !answerText.trim() || !questions.trim()
          ? "Enter a patient answer and at least one question example."
          : content?.verificationState !== "passed" &&
              !(
                issues.length &&
                issues.every(
                  ([key]) =>
                    subjectiveChecks.includes(key) && overrides.includes(key),
                ) &&
                note.trim()
              )
            ? "This answer needs correction or checking. Review the issues below and select Save and check."
            : overrides.length && !note.trim()
              ? "Add a review note explaining your wording attestation."
              : undefined;
  const comparisons =
    answer && data
      ? [
          ...new Set([
            ...(answer.wordingConflicts || []).map((c) => c.answerId),
            ...(answer.grouping?.proposals || []).map((p) => p.targetId),
          ]),
        ].flatMap((id) => {
          const target = data.answers.find((a) => a.id === id);
          if (!target || ["rejected", "outdated"].includes(target.availability))
            return [];
          const proposal = answer.grouping?.proposals.find(
            (p) => p.targetId === id,
          );
          const shared =
            answer.wordingConflicts?.find((c) => c.answerId === id)?.wordings ||
            [];
          const stale =
            !proposal ||
            target.reviewVersion !== proposal.targetReviewVersion ||
            ["pending", "processing"].includes(target.processingStatus || "") ||
            !!(target.draft && target.published);
          const equivalent =
            proposal?.relation === "equivalent" &&
            proposal.content?.verificationState === "passed" &&
            !stale &&
            answer.source === "session" &&
            !answer.published;
          return [{ target, proposal, shared, equivalent, stale }];
        })
      : [];
  const availableCount =
    data?.answers.filter((a) => a.availability.startsWith("available"))
      .length || 0;
  const awaitingApprovalCount =
    data?.answers.filter((a) => answerReviewStatus(a).attention === "approval")
      .length || 0;
  const needsCorrectionCount =
    data?.answers.filter(
      (a) => answerReviewStatus(a).attention === "correction",
    ).length || 0;
  if (
    (evidenceOpen || supportReviewOpen) &&
    data?.sourceEvidence &&
    answer &&
    content
  ) {
    return (
      <SemanticEvidencePanel
        key={answer.id}
        revision={`${answer.reviewVersion}:${data.libraryRevision}`}
        source={data.sourceEvidence}
        answerText={answerText}
        evidence={content.evidence}
        claims={content.checks.factSupported?.unsupportedClaims}
        sourceKind={answer.source}
        busy={busy}
        reviewError={error}
        reassessment={supportReviewOpen}
        failureReason={content.checks.factSupported?.reason}
        onClose={() => {
          setEvidenceOpen(false);
          setSupportReviewOpen(false);
        }}
        onUse={(references, explanation) =>
          act(
            "validate",
            undefined,
            [],
            references.length ? references : undefined,
            undefined,
            explanation,
          )
        }
      />
    );
  }
  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Semantic responses — {title}</DialogTitle>
      <DialogContent dividers>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Review the patient answers that can be reused in this case. Predicted
          answers can pass automatically; answers collected from sessions need
          your approval.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}
        {data?.error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Prediction issue: {data.error}
          </Alert>
        )}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ sm: "center" }}
          sx={{ mb: 2 }}
        >
          <Typography sx={{ flex: 1 }}>
            <Tooltip
              describeChild
              title="Published answers eligible for reuse during diagnosis. An answer with pending changes can appear here and in a review count: its earlier published version stays available. Matching and conversation checks still apply. Counts cover the whole library regardless of filters."
            >
              <Box component="span" tabIndex={0} sx={statusHelpSx}>
                {availableCount} available
              </Box>
            </Tooltip>{" "}
            ·{" "}
            <Tooltip
              describeChild
              title="Verified answers or changes requiring administrator approval. A previously published answer remains available while its changes await approval."
            >
              <Box component="span" tabIndex={0} sx={statusHelpSx}>
                {awaitingApprovalCount} awaiting approval
              </Box>
            </Tooltip>{" "}
            ·{" "}
            <Tooltip
              describeChild
              title="Answers or changes requiring correction, evidence review, or a retry after failed background checks. Open the question to review the issues. A previously published answer remains available."
            >
              <Box component="span" tabIndex={0} sx={statusHelpSx}>
                {needsCorrectionCount} needs attention
              </Box>
            </Tooltip>{" "}
            ·{" "}
            <Tooltip
              describeChild
              title="Prediction describes the background generation job. Ready means generation finished; each answer has its own verification and availability status."
            >
              <Box component="span" tabIndex={0} sx={statusHelpSx}>
                Prediction: {data?.status || "Loading"}
              </Box>
            </Tooltip>
          </Typography>
          <Button
            disabled={busy}
            onClick={() => void load().catch((e) => setError(e.message))}
          >
            Refresh
          </Button>
          <Button
            disabled={
              busy ||
              data?.status === "processing" ||
              data?.status === "pending"
            }
            onClick={() => void regenerate()}
          >
            Regenerate predictions
          </Button>
        </Stack>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "340px minmax(0,1fr)" },
            gap: 3,
          }}
        >
          <Box>
            <TextField
              label="Search questions and answers"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 1 }}
            />
            <Stack direction="row" spacing={1}>
              <TextField
                select
                label="Availability"
                slotProps={{
                  select: {
                    SelectDisplayProps: {
                      "aria-label": "Availability",
                      "aria-labelledby": undefined,
                    },
                  },
                }}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="current">Current answers</MenuItem>
                <MenuItem value="all">All, including outdated</MenuItem>
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="review">Needs review</MenuItem>
                {Object.entries(availabilityLabels)
                  .filter(([key]) => !key.startsWith("available"))
                  .map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
              </TextField>
              <TextField
                select
                label="Source"
                slotProps={{
                  select: {
                    SelectDisplayProps: {
                      "aria-label": "Source",
                      "aria-labelledby": undefined,
                    },
                  },
                }}
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="all">All sources</MenuItem>
                {Object.entries(sourceLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Checkbox
                  checked={hideRejected}
                  onChange={(_, checked) => setHideRejected(checked)}
                />
              }
              label="Hide rejected questions and answers"
            />
            <List
              ref={questionList}
              sx={{ maxHeight: "65vh", overflow: "auto" }}
              aria-label="Case questions"
            >
              {visible.map((a) => {
                const status = answerReviewStatus(a);
                const automaticCount =
                  automaticQuestionsByAnswer.get(a.id)?.length || 0;
                const additions =
                  questionGroups
                    .find((group) => group.answer.id === a.id)
                    ?.members.filter(
                      (member) =>
                        member.id !== a.id &&
                        (!hideRejected || member.availability !== "rejected"),
                    ) || [];
                const needsReview = additions.filter(
                  (member) => !!answerReviewStatus(member).attention,
                );
                return (
                  <ListItemButton
                    key={a.id}
                    selected={(answer?.questionGroupId || selected) === a.id}
                    onClick={() => {
                      if (dirty.current && a.id !== selected) {
                        setError(
                          "Save and check your edits before reviewing another exchange.",
                        );
                        return;
                      }
                      setSelected(a.id);
                    }}
                    disabled={busy}
                    sx={{
                      alignItems: "flex-start",
                      borderLeft: "4px solid",
                      borderLeftColor: status.attention
                        ? `${status.color}.main`
                        : "transparent",
                      mb: 0.5,
                    }}
                  >
                    <ListItemText
                      primary={a.question}
                      secondaryTypographyProps={{ component: "div" }}
                      secondary={
                        <Box sx={{ mt: 0.75 }}>
                          <Tooltip describeChild title={status.description}>
                            <Chip
                              label={status.label}
                              color={status.color}
                              size="small"
                              tabIndex={0}
                              sx={{
                                fontWeight: status.attention ? 700 : 500,
                                cursor: "help",
                                maxWidth: "100%",
                                height: "auto",
                                minHeight: 24,
                                "& .MuiChip-label": {
                                  whiteSpace: "normal",
                                  py: 0.25,
                                },
                              }}
                            />
                          </Tooltip>
                          {!!needsReview.length && (
                            <Chip
                              label={`${needsReview.length} collected ${needsReview.length === 1 ? "exchange needs" : "exchanges need"} review`}
                              size="small"
                              color={
                                needsReview.some(
                                  (member) =>
                                    answerReviewStatus(member).attention ===
                                    "correction",
                                )
                                  ? "error"
                                  : "warning"
                              }
                              sx={{
                                mt: 0.75,
                                maxWidth: "100%",
                                height: "auto",
                                "& .MuiChip-label": {
                                  whiteSpace: "normal",
                                  py: 0.25,
                                },
                              }}
                            />
                          )}
                          {!!automaticCount && (
                            <Tooltip
                              describeChild
                              title="Added from equivalent session exchanges after verification. The existing patient answer was retained."
                            >
                              <Chip
                                label={`${automaticCount} automatically approved ${automaticCount === 1 ? "phrasing" : "phrasings"}`}
                                color="success"
                                variant="outlined"
                                size="small"
                                sx={{
                                  mt: 0.75,
                                  maxWidth: "100%",
                                  height: "auto",
                                  "& .MuiChip-label": {
                                    whiteSpace: "normal",
                                    py: 0.25,
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                          {!!additions.length && !needsReview.length && (
                            <Typography variant="caption" display="block">
                              {additions.length} collected{" "}
                              {additions.length === 1
                                ? "exchange"
                                : "exchanges"}
                            </Typography>
                          )}
                          {a.published &&
                            a.draft &&
                            a.availability.startsWith("available") && (
                              <Typography
                                variant="caption"
                                display="block"
                                sx={{ mt: 0.5 }}
                              >
                                Published answer still available
                              </Typography>
                            )}
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {sourceLabels[a.source]}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
            {!visible.length && (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                No answers match these filters.
              </Typography>
            )}
          </Box>
          <Box>
            {!answer && (
              <Typography>
                Select a question to review its answer. Newly generated and
                collected answers will appear here.
              </Typography>
            )}
            {answer && content && (
              <>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {answer.question}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  sx={{ mb: 1 }}
                >
                  <Tooltip describeChild title={reviewStatus?.description}>
                    <Chip
                      tabIndex={0}
                      sx={{ cursor: "help" }}
                      label={reviewStatus?.label}
                      color={reviewStatus?.color || "default"}
                    />
                  </Tooltip>
                  <Chip
                    label={sourceLabels[answer.source]}
                    variant="outlined"
                  />
                </Stack>
                <Alert
                  severity={
                    reviewStatus?.color === "default"
                      ? "info"
                      : reviewStatus?.color || "info"
                  }
                  sx={{ mb: 2 }}
                >
                  {reviewStatus?.attention
                    ? reviewStatus.description
                    : answer.availabilityReason}
                </Alert>
                {answer.source === "session" && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Observed {answer.occurrenceCount} time
                    {answer.occurrenceCount === 1 ? "" : "s"} · Reused{" "}
                    {answer.usageCount} times
                  </Typography>
                )}
                {groupMembers.some(
                  (member) => member.id !== selectedQuestionGroup?.answer.id,
                ) && (
                  <Box
                    component="section"
                    aria-label="Collected exchanges for this question"
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2,
                      mb: 2,
                    }}
                  >
                    <Typography fontWeight={600}>
                      Collected exchanges for this question
                    </Typography>
                    <Typography variant="body2" sx={{ my: 1 }}>
                      Equivalent answers merge automatically after verification.
                      Alternative answers and uncertain matches remain here for
                      review.
                    </Typography>
                    <Stack spacing={1}>
                      {groupMembers.map((member) => (
                        <Button
                          key={member.id}
                          variant={
                            member.id === selected ? "contained" : "outlined"
                          }
                          disabled={busy}
                          onClick={() => {
                            if (dirty.current && member.id !== selected) {
                              setError(
                                "Save and check your edits before reviewing another exchange.",
                              );
                              return;
                            }
                            setSelected(member.id);
                          }}
                          sx={{
                            textAlign: "left",
                            justifyContent: "flex-start",
                            textTransform: "none",
                          }}
                        >
                          <Box>
                            <Typography variant="body2">
                              {member.questionGroupId
                                ? member.question
                                : "Existing question and answer"}
                            </Typography>
                            <Typography variant="caption">
                              {answerReviewStatus(member).label}
                            </Typography>
                          </Box>
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                )}
                {answer.processingError && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Background checking: {answer.processingError}
                    {checking
                      ? " A retry is queued."
                      : " Use Save and check to retry."}
                  </Alert>
                )}
                {answer.grouping?.proposals
                  .filter(
                    (proposal) =>
                      proposal.relation === "equivalent" && proposal.mergeCheck,
                  )
                  .map((proposal) => (
                    <SemanticMergeDiagnostics
                      key={proposal.targetId}
                      check={proposal.mergeCheck!}
                    />
                  ))}
                {lastAutomaticMerge?.mergeCheck && (
                  <SemanticMergeDiagnostics
                    check={lastAutomaticMerge.mergeCheck}
                  />
                )}
                {comparisons.map(
                  ({ target, proposal, shared, equivalent, stale }) => {
                    const effects = (
                      conflicts: WordingConflict[],
                    ): AnswerEffect[] =>
                      conflicts.map((c) => ({
                        id: c.answerId,
                        question: c.question,
                        answerText: c.answerText,
                        wordings: c.wordings,
                        remainingQuestions: Math.max(
                          0,
                          (data?.answers.find((a) => a.id === c.answerId)
                            ?.published?.questionExamples.length || 0) -
                            c.wordings.length,
                        ),
                      }));
                    const combineConflicts = conflictsFor(target.id);
                    const multipleExisting =
                      (answer.wordingConflicts || []).filter((c) =>
                        shared.some((q) =>
                          c.wordings.some(
                            (other) =>
                              reviewQuestionKey(q) === reviewQuestionKey(other),
                          ),
                        ),
                      ).length > 1;
                    const existingContent =
                      target.published || target.draft || target;
                    const existingKeys = new Set(
                      existingContent.questionExamples.map(reviewQuestionKey),
                    );
                    const incomingQuestions = uniqueReviewQuestions(
                      equivalent
                        ? proposal?.content?.questionExamples || []
                        : [
                            answer.question,
                            ...(content.questionChecks.length
                              ? content.questionChecks.map((q) => q.question)
                              : content.questionExamples),
                          ],
                    ).filter((q) => !existingKeys.has(reviewQuestionKey(q)));
                    const mergeBlocked = editsChanged
                      ? "Save and check your edits before merging questions."
                      : checking ||
                          ["pending", "processing"].includes(
                            target.processingStatus || "",
                          )
                        ? "Wait for background checking to finish before merging questions."
                        : target.availability === "outdated" ||
                            target.availability === "rejected"
                          ? "Choose a current, unrejected answer to keep."
                          : target.published && target.draft
                            ? "Review the existing answer’s pending edits before adding questions."
                            : !incomingQuestions.length
                              ? "These questions are already attached to the existing answer."
                              : undefined;
                    return (
                      <SemanticAnswerComparison
                        key={`${answer.id}:${target.id}`}
                        question={answer.question}
                        questions={shared.length ? shared : [answer.question]}
                        currentAnswer={
                          (target.published || target.draft || target)
                            .answerText
                        }
                        proposedAnswer={answerText}
                        currentAvailable={
                          !!target.published &&
                          target.availability.startsWith("available")
                        }
                        anotherAvailable={
                          (!!answer.published && shared.length > 0) ||
                          multipleExisting
                        }
                        multipleExisting={multipleExisting}
                        currentQuestion={
                          target.published?.questionExamples[0] ||
                          target.question
                        }
                        equivalent={equivalent}
                        conflict={proposal?.relation === "conflict"}
                        matchedQuestion={proposal?.matchedQuestion}
                        reason={proposal?.reason}
                        busy={busy}
                        version={`${data?.libraryRevision}:${answer.reviewVersion}:${data?.answers.map((a) => `${a.id}:${a.reviewVersion}:${a.availability}`).join("|")}`}
                        combineBlocked={
                          approvalBlocked ||
                          (unresolvedAlternative
                            ? "Resolve the conflicting answer first."
                            : stale
                              ? "The existing answer changed. Save and check this answer again before combining."
                              : !equivalent
                                ? "Equivalence has not been verified. Save and check first."
                                : undefined)
                        }
                        combinedEffects={effects(combineConflicts)}
                        proposedQuestions={uniqueReviewQuestions([
                          answer.question,
                          ...content.questionExamples,
                        ])}
                        mergeQuestions={incomingQuestions}
                        existingQuestions={existingContent.questionExamples}
                        mergeBlocked={mergeBlocked}
                        existingSource={target.source}
                        onChoose={async (
                          choice: AnswerChoice,
                          selectedQuestions?: string[],
                        ) => {
                          if (choice === "mergeQuestions") {
                            if (mergeBlocked || !data) return false;
                            setBusy(true);
                            readEpoch.current++;
                            setError("");
                            setMessage("");
                            try {
                              const questionExamples = mergeReviewQuestions(
                                existingContent.questionExamples,
                                incomingQuestions,
                                selectedQuestions || [],
                              );
                              // Reuse ordinary asynchronous verification of the target.
                              // Never borrow the reviewed answer’s checks or approval.
                              const payload = await request(
                                `answers/${target.id}/review`,
                                {
                                  revision: data.libraryRevision,
                                  reviewVersion: target.reviewVersion,
                                  action: "validate",
                                  answerText: existingContent.answerText,
                                  questionExamples,
                                  note:
                                    note.trim() ||
                                    `Check selected questions from answer ${answer.id} against the unchanged existing answer.`,
                                },
                              );
                              dirty.current = false;
                              acceptData(payload.data);
                              showMergedAnswer(target.id);
                              setMessage(
                                "Existing answer kept. Selected questions are being checked in the background. " +
                                  (target.source === "session"
                                    ? "Approve the verified changes before reuse. "
                                    : "Questions that pass can become available automatically. ") +
                                  "The answer you were reviewing has not been changed.",
                              );
                              return true;
                            } catch (e) {
                              setError(
                                e instanceof Error
                                  ? e.message
                                  : "Unable to check selected questions",
                              );
                              return false;
                            } finally {
                              setBusy(false);
                            }
                          }
                          if (choice === "keep") {
                            const saved = await act("reject");
                            if (saved)
                              setMessage(
                                multipleExisting
                                  ? "Proposed answer discarded. Review the existing answers to choose one response."
                                  : target.published
                                    ? "Current answer kept. The proposed answer was rejected."
                                    : "Existing answer kept; it still needs approval. The proposed answer was rejected.",
                              );
                            return saved;
                          }
                          if (choice === "combine") {
                            const selectedKeys = new Set(
                              [
                                ...existingContent.questionExamples,
                                ...(selectedQuestions || []),
                              ].map(reviewQuestionKey),
                            );
                            const relevantConflicts = combineConflicts.filter(
                              (conflict) =>
                                conflict.wordings.some((question) =>
                                  selectedKeys.has(reviewQuestionKey(question)),
                                ),
                            );
                            return act(
                              "merge",
                              target.id,
                              relevantConflicts,
                              undefined,
                              selectedQuestions,
                            );
                          }
                          setMessage(
                            "Both question and answer records are retained separately. Review the proposed pair below: correct conflicting facts or clarify the question scope, then select Save and check. Rejected answers still need successful checks and approval before reuse.",
                          );
                          answerInput.current?.focus();
                          answerInput.current?.scrollIntoView({
                            block: "center",
                            behavior: "smooth",
                          });
                          return true;
                        }}
                        onReviewCurrent={() => {
                          setSourceFilter("all");
                          setFilter("current");
                          setSearch("");
                          setSelected(target.id);
                        }}
                      />
                    );
                  },
                )}
                {answer.draft && answer.published && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Currently reused answer: “{answer.published.answerText}”
                    <br />
                    The edits below have not replaced it.
                  </Alert>
                )}
                <TextField
                  label="Patient answer"
                  inputRef={answerInput}
                  helperText="The exact words the patient will say."
                  value={answerText}
                  onChange={(e) => {
                    dirty.current = true;
                    setAnswerText(e.target.value);
                  }}
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={busy || unavailable}
                />
                <TextField
                  label="Equivalent question phrasings"
                  helperText="One question per line. Each must receive this same answer."
                  value={questions}
                  onChange={(e) => {
                    dirty.current = true;
                    setQuestions(e.target.value);
                  }}
                  fullWidth
                  multiline
                  minRows={4}
                  disabled={busy || unavailable}
                  sx={{ mt: 2 }}
                />
                {!!automaticQuestions.length && (
                  <Box
                    component="section"
                    aria-label="Automatically approved phrasings"
                    sx={{
                      mt: 2,
                      border: 1,
                      borderColor: "success.main",
                      borderRadius: 2,
                      p: 1.5,
                    }}
                  >
                    <Typography variant="subtitle2">
                      Automatically approved phrasings
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ mt: 0.5, mb: 1 }}
                    >
                      These phrasings are published. Any edits above still need
                      checking.
                    </Typography>
                    <Stack spacing={1}>
                      {automaticQuestions.map((question) => (
                        <Stack
                          key={question}
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          alignItems={{ xs: "flex-start", sm: "center" }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ flex: 1, overflowWrap: "anywhere" }}
                          >
                            {question}
                          </Typography>
                          <Chip
                            label="Automatically approved"
                            color="success"
                            variant="outlined"
                            size="small"
                          />
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}
                {!!issues.length && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">
                      What needs attention
                    </Typography>
                    {issues.map(([key, c]) => (
                      <Box key={key} sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          <strong>{checkLabels[key] || key}:</strong> {c.reason}
                        </Typography>
                        {key === "factSupported" && (
                          <>
                            {!!c.unsupportedClaims?.length && (
                              <Typography
                                variant="body2"
                                sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                              >
                                {highlightedText(
                                  content.answerText,
                                  answerHighlights(
                                    content.answerText,
                                    c.unsupportedClaims,
                                  ),
                                )}
                              </Typography>
                            )}
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{ mt: 1, flexWrap: "wrap" }}
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={
                                  busy ||
                                  unavailable ||
                                  checking ||
                                  !data?.sourceEvidence
                                }
                                onClick={() => {
                                  setError("");
                                  setSupportReviewOpen(true);
                                }}
                              >
                                This answer is supported
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={
                                  busy || unavailable || !data?.sourceEvidence
                                }
                                onClick={() => {
                                  setError("");
                                  setEvidenceOpen(true);
                                }}
                              >
                                Resolve source evidence
                              </Button>
                              <Button
                                size="small"
                                onClick={() => answerInput.current?.focus()}
                              >
                                Edit the answer
                              </Button>
                            </Stack>
                          </>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
                <Box component="details" sx={{ mt: 2 }}>
                  <Box component="summary" sx={{ cursor: "pointer" }}>
                    Source evidence
                  </Box>
                  {content.evidence?.length ? (
                    content.evidence.map((s, n) => (
                      <Box
                        key={n}
                        sx={{ my: 1, p: 1, bgcolor: "action.hover" }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {s.quote}
                        </Typography>
                        {s.path && (
                          <Typography variant="caption">
                            Case field: {s.path}
                          </Typography>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2">
                      Supporting evidence has not been verified yet.
                    </Typography>
                  )}
                </Box>
                {answer.source === "session" && (
                  <Box component="details" sx={{ mt: 2 }}>
                    <Box component="summary" sx={{ cursor: "pointer" }}>
                      Original session context
                    </Box>
                    {answer.conversationContext?.length ? (
                      answer.conversationContext.map((exchange, n) => (
                        <Box key={n} sx={{ my: 1 }}>
                          <Typography variant="body2">
                            Question: {exchange.question}
                          </Typography>
                          <Typography variant="body2">
                            Patient: {exchange.response}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2">
                        There were no earlier exchanges.
                      </Typography>
                    )}
                  </Box>
                )}
                <Box component="details" sx={{ mt: 2 }}>
                  <Box component="summary" sx={{ cursor: "pointer" }}>
                    Question checks and detailed verification
                    {failedCheckCount > 0 && (
                      <Box
                        component="span"
                        sx={{ color: "error.main", fontWeight: 700 }}
                      >
                        {" "}
                        · {failedCheckCount} failed
                      </Box>
                    )}
                    {evidenceReviewCount > 0 && (
                      <Box
                        component="span"
                        sx={{ ml: 1, color: "warning.main", fontWeight: 600 }}
                      >
                        · {evidenceReviewCount} needs evidence review
                      </Box>
                    )}
                    {uncheckedCount > 0 && (
                      <Box
                        component="span"
                        sx={{ color: "warning.main", fontWeight: 700 }}
                      >
                        {" "}
                        · {uncheckedCount} not checked
                      </Box>
                    )}
                  </Box>
                  {Object.entries(content.checks || {})
                    .sort(
                      ([, a], [, b]) =>
                        ({ failed: 0, missing: 1, passed: 2 })[a.status] -
                        { failed: 0, missing: 1, passed: 2 }[b.status],
                    )
                    .map(([key, check]) => (
                      <VerificationCheck
                        key={key}
                        label={checkLabels[key] || key}
                        check={check}
                      />
                    ))}
                  <Divider sx={{ my: 1 }} />
                  {content.questionChecks?.map((check, n) => (
                    <VerificationCheck
                      key={n}
                      label={`“${check.question}”`}
                      check={check}
                    />
                  ))}
                  {issues
                    .filter(([key]) => subjectiveChecks.includes(key))
                    .map(([key]) => (
                      <FormControlLabel
                        key={key}
                        control={
                          <Checkbox
                            checked={overrides.includes(key)}
                            disabled={busy || unavailable}
                            onChange={(e) =>
                              setOverrides((v) =>
                                e.target.checked
                                  ? [...v, key]
                                  : v.filter((k) => k !== key),
                              )
                            }
                          />
                        }
                        label={`I confirm ${checkLabels[key].toLowerCase()} is appropriate. Explain why in the note.`}
                      />
                    ))}
                </Box>
                <TextField
                  fullWidth
                  multiline
                  label={
                    overrides.length
                      ? "Review note (required for attestation)"
                      : "Review note (optional)"
                  }
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={busy || unavailable}
                  sx={{ mt: 2 }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {reviewActionExplanation(answer.source, "validate")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {comparisons.length
                    ? "Choose between the answers above. After editing, select Save and check to verify your changes."
                    : "Approve publishes the verified answer. Save and check edited answers first. Reject removes it from reuse."}{" "}
                  Unsupported evidence and disclosure issues must be corrected.
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 2 }}
                  flexWrap="wrap"
                >
                  <Button
                    variant="outlined"
                    disabled={
                      busy ||
                      unavailable ||
                      !answerText.trim() ||
                      !questions.trim()
                    }
                    onClick={() => void act("validate")}
                  >
                    Save and check
                  </Button>
                  {comparisons.length === 0 && (
                    <>
                      <Button
                        variant="contained"
                        disabled={
                          busy ||
                          unavailable ||
                          checking ||
                          editsChanged ||
                          unresolvedAlternative ||
                          comparisons.length > 0 ||
                          !answerText.trim() ||
                          !questions.trim() ||
                          (!!overrides.length && !note.trim())
                        }
                        onClick={() => void act("approve")}
                      >
                        Approve
                      </Button>
                      <Button
                        color="error"
                        disabled={busy || unavailable}
                        onClick={() => void act("reject")}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                </Stack>
                <Box component="details" sx={{ mt: 2 }}>
                  <Box component="summary" sx={{ cursor: "pointer" }}>
                    Review history
                  </Box>
                  {answer.reviewHistory.length ? (
                    answer.reviewHistory.map((e, n) => (
                      <Typography variant="body2" key={n} sx={{ my: 1 }}>
                        {e.at} ·{" "}
                        {e.actor === "system"
                          ? "System"
                          : `Administrator ${e.actor}`}{" "}
                        ·{" "}
                        {e.action === "questions_auto_merged"
                          ? "Questions merged automatically"
                          : e.action}
                        {e.note ? `: ${e.note}` : ""}
                        {e.evidenceExplanation && (
                          <Box
                            component="span"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            Evidence reassessment: {e.evidenceExplanation}
                          </Box>
                        )}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="body2">
                      No administrator decisions yet.
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
        <Box component="details" sx={{ mt: 3 }}>
          <Box component="summary" sx={{ cursor: "pointer" }}>
            Prediction coverage and generation details
          </Box>
          <Typography variant="body2" sx={{ my: 1 }}>
            Prediction is bounded to 80 answer groups. Missing coverage does not
            imply the case lacks that information.
          </Typography>
          <Box
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              fontSize: 12,
            }}
          >
            {JSON.stringify(
              {
                coverage: data?.coverage,
                prediction: data?.artifacts?.libraryPrediction,
              },
              null,
              2,
            )}
          </Box>
          <Box component="details">
            <Box component="summary">Previous artifacts and decisions</Box>
            <Box
              component="pre"
              sx={{
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                fontSize: 12,
              }}
            >
              {JSON.stringify(
                {
                  history: data?.artifacts?.history,
                },
                null,
                2,
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Typography variant="body2" sx={{ mr: "auto", ml: 2 }}>
          {busy
            ? "Saving…"
            : checking
              ? `Background ${answer?.processingStage === "grouping" ? "paraphrase grouping" : answer?.processingStage === "embeddings" ? "embedding preparation" : answer?.processingStage === "publication" ? "publication" : "verification"} ${answer?.processingStatus === "pending" ? "queued" : "running"}…`
              : ""}
        </Typography>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
